import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  type EmailSendingOverviewRow,
  useEmailSendingOverview,
  useSetEmailMonthlyQuota,
  useSetEmailSendingPaused,
} from '@/hooks/useEmailSettings';

const pct = (n: number, d: number) => (d > 0 ? (100 * n) / d : 0);

function RateCell({ count, total, warnAt }: { count: number; total: number; warnAt: number }) {
  const rate = pct(count, total);
  return (
    <span className={cn('tabular-nums', total >= 50 && rate > warnAt ? 'font-semibold text-destructive' : '')}>
      {total > 0 ? `${rate.toFixed(rate < 1 ? 2 : 1)}%` : '—'}
      <span className="ml-1 text-xs text-muted-foreground">({count})</span>
    </span>
  );
}

function QuotaEditor({ row, onSave, saving }: { row: EmailSendingOverviewRow; onSave: (quota: number) => void; saving: boolean }) {
  const [value, setValue] = useState(String(row.monthly_quota));
  const changed = Number(value) !== row.monthly_quota && /^\d+$/.test(value);
  return (
    <div className="flex items-center gap-1">
      <Input className="h-7 w-20 text-xs" value={value} onChange={(e) => setValue(e.target.value)} inputMode="numeric" />
      {changed && (
        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => onSave(Number(value))} disabled={saving}>
          Set
        </Button>
      )}
    </div>
  );
}

export function EmailSendingOverviewCard({ enabled }: { enabled: boolean }) {
  const { data, isLoading } = useEmailSendingOverview(enabled);
  const setPaused = useSetEmailSendingPaused();
  const setQuota = useSetEmailMonthlyQuota();

  const togglePause = async (row: EmailSendingOverviewRow) => {
    const paused = !row.sending_paused;
    const reason = paused ? window.prompt(`Reason for pausing ${row.company_name}?`, 'Manual: reviewing bounce rate') ?? undefined : undefined;
    if (paused && reason === undefined) return;
    try {
      await setPaused.mutateAsync({ companyId: row.company_id, paused, reason });
      toast.success(paused ? `${row.company_name} paused` : `${row.company_name} sending resumed`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Update failed');
    }
  };

  const saveQuota = async (row: EmailSendingOverviewRow, quota: number) => {
    try {
      await setQuota.mutateAsync({ companyId: row.company_id, quota, reason: 'Set by super admin' });
      toast.success(`${row.company_name} quota set to ${quota.toLocaleString()}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Update failed');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Email Sending
        </CardTitle>
        <CardDescription>
          Per-tenant volume, quota and 7-day reputation. The hourly guard pauses any tenant over 5% bounces or 0.1% complaints (min. 50 emails); unpause here once they've fixed their list.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !data?.length ? (
          <p className="text-sm text-muted-foreground">No companies yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Identity</TableHead>
                  <TableHead className="text-right">This month</TableHead>
                  <TableHead>Quota</TableHead>
                  <TableHead>Bounces 7d</TableHead>
                  <TableHead>Complaints 7d</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((row) => (
                  <TableRow key={row.company_id}>
                    <TableCell>
                      <div className="font-medium">{row.company_name}</div>
                      <div className="font-mono text-xs text-muted-foreground">{row.public_key}</div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {row.custom_domain_status === 'verified' ? (
                        <span className="font-mono">{row.custom_domain}</span>
                      ) : row.custom_domain_status === 'pending' ? (
                        <span className="text-muted-foreground">{row.custom_domain} (pending)</span>
                      ) : (
                        <span className="text-muted-foreground">platform</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.sent_this_month.toLocaleString()}
                      <span className="ml-1 text-xs text-muted-foreground">/ {row.sent_7d} in 7d</span>
                    </TableCell>
                    <TableCell>
                      <QuotaEditor row={row} onSave={(q) => saveQuota(row, q)} saving={setQuota.isPending} />
                    </TableCell>
                    <TableCell>
                      <RateCell count={row.bounced_7d} total={row.sent_7d} warnAt={5} />
                    </TableCell>
                    <TableCell>
                      <RateCell count={row.complained_7d} total={row.sent_7d} warnAt={0.1} />
                    </TableCell>
                    <TableCell>
                      {row.sending_paused ? (
                        <Badge variant="destructive" title={row.paused_reason ?? undefined}>
                          Paused
                        </Badge>
                      ) : (
                        <Badge variant="default">Sending</Badge>
                      )}
                      {row.sending_paused && row.paused_reason && (
                        <div className="mt-1 max-w-[220px] text-xs text-muted-foreground">{row.paused_reason}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant={row.sending_paused ? 'default' : 'outline'} onClick={() => togglePause(row)} disabled={setPaused.isPending}>
                        {row.sending_paused ? 'Unpause' : 'Pause'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
